/**
 * One-time, tab-bound handoff of non-secret route state into a new iPortal tab (#897).
 *
 * history.state and Pinia are tab-local, so a plain URL would lose the selection context of
 * the action result (e.g. detailQueryKeyValue, selectedRow). The opener writes the state into
 * the sessionStorage of the window it reserved in the user click (same origin, about:blank)
 * and then navigates it. The new tab consumes the entry exactly once at boot (before the
 * router's initial navigation), validates version/expiry/receiver path, removes it and merges
 * the allowed keys into history.state - the same place the same-window navigation uses.
 *
 * Never put tokens or passwords into the state; nothing is written to localStorage, URLs or logs.
 */

export const HANDOFF_STORAGE_KEY = 'iportal.actionResultHandoff'
export const HANDOFF_VERSION = 1
export const HANDOFF_TTL_MS = 60 * 1000

// route state keys the existing route components read from history.state
export const HANDOFF_STATE_KEYS = Object.freeze([
  'detailQueryKeyValue',
  'selectedRow',
  'selectedMultiple',
  'objInstanceID',
  'queryParameter',
  'readonly',
  'postAction',
  'actionSetupToken',
  'wedata',
  'actionResultPlugin', // plugin view of a result tab (route 'Plugin', Plugin.vue)
  'actionResultNewTab' // marker: first entry of a result tab, no back navigation (Formular.vue)
])

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function filterHandoffState(state) {
  const result = {}
  if (!isPlainObject(state)) return result
  HANDOFF_STATE_KEYS.forEach(key => {
    if (state[key] !== undefined) {
      result[key] = state[key]
    }
  })
  return result
}

/**
 * Reserve a new browser tab synchronously inside the user click. Returns null when the browser
 * (popup blocker) refused it - callers must report that and must not fall back silently.
 */
export function reserveResultWindow(win, loadingText) {
  let reserved = null
  try {
    reserved = win.open('', '_blank')
  } catch (e) {
    reserved = null
  }
  if (!reserved) {
    return null
  }
  try {
    if (loadingText && reserved.document && reserved.document.body) {
      reserved.document.title = loadingText
      reserved.document.body.textContent = loadingText
    }
  } catch (e) {
    // a not yet accessible document is no error - the window is navigated afterwards
  }
  return reserved
}

export function closeReservedWindow(reserved) {
  try {
    if (reserved && !reserved.closed) {
      reserved.close()
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Navigate the reserved window to an internal iPortal URL and hand over the route state.
 * Throws when the target is not the own origin or the reserved window is not accessible.
 */
export function navigateReservedWindow(reserved, href, state, {origin, now = Date.now()} = {}) {
  if (!reserved || reserved.closed) {
    throw new Error('reserved window not available')
  }
  const target = new URL(href, origin)
  if (target.origin !== new URL(origin).origin) {
    throw new Error('handoff target is not same-origin')
  }

  const handoffState = filterHandoffState(state)
  if (Object.keys(handoffState).length > 0) {
    const payload = {
      v: HANDOFF_VERSION,
      target: target.pathname + target.search,
      expires: now + HANDOFF_TTL_MS,
      state: handoffState
    }
    // same-origin about:blank window: its sessionStorage belongs to the new tab only
    reserved.sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(payload))
  }
  try {
    reserved.opener = null
  } catch (e) {
    // ignore
  }
  reserved.location.replace(target.href)
}

/**
 * Navigate the reserved window to an external (or arbitrary) URL without any state handoff.
 */
export function navigateReservedWindowExternal(reserved, href) {
  if (!reserved || reserved.closed) {
    throw new Error('reserved window not available')
  }
  try {
    reserved.opener = null
  } catch (e) {
    // ignore
  }
  reserved.location.replace(href)
}

/**
 * Read, remove and validate the handoff in the new tab. Returns the filtered state or null.
 */
export function consumeHandoff(win, now = Date.now()) {
  let storage
  let raw
  try {
    storage = win.sessionStorage
    raw = storage.getItem(HANDOFF_STORAGE_KEY)
  } catch (e) {
    return null
  }
  if (raw === null || raw === undefined) {
    return null
  }
  try {
    storage.removeItem(HANDOFF_STORAGE_KEY) // one-time, even if invalid
  } catch (e) {
    return null
  }

  let payload
  try {
    payload = JSON.parse(raw)
  } catch (e) {
    return null
  }
  if (!isPlainObject(payload) || payload.v !== HANDOFF_VERSION) return null
  if (typeof payload.expires !== 'number' || now > payload.expires) return null
  const receiver = win.location.pathname + win.location.search
  if (payload.target !== receiver) return null

  const state = filterHandoffState(payload.state)
  return Object.keys(state).length > 0 ? state : null
}

/**
 * Merge the consumed state into history.state (before the router's initial navigation, which
 * keeps existing history.state entries).
 */
export function applyHandoffState(win, state) {
  if (!state) return false
  const current = isPlainObject(win.history.state) ? win.history.state : {}
  win.history.replaceState({...current, ...state}, '')
  return true
}
